import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { api } from "../../api/api";

/**
 * Analytics thunks for dashboard components
 * Note: fetchCustomerAcquisitionData has been moved to acquisitionThunk.ts to resolve build issues.
 */

export const fetchPolicyDistribution = createAsyncThunk(
  "dashboard/fetchPolicyDistribution",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/analytics/get-analytics-pie-chart");
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching policy distribution");
    }
  }
);

export const fetchRecordCounts = createAsyncThunk(
  "dashboard/fetchRecordCounts",
  async (
    {
      tableName,
      column,
      fromDate,
      toDate,
      key,
      advanceFilter,
      status,
    }: {
      tableName: string;
      column: string;
      fromDate?: string;
      toDate?: string;
      key?: string;
      advanceFilter?: any;
      status?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      let url = `/general/get-record-counts?table_name=${tableName}&column=${column}`;
      if (status) url += `&status=${status}`;
      if (fromDate) url += `&from_date=${fromDate}&advanced_filter=true`;
      if (toDate) url += `&to_date=${toDate}&advanced_filter=true`;
      if (advanceFilter) {
        url += `&advanceFilter=${encodeURIComponent(JSON.stringify(advanceFilter))}`;
      }

      const response = await api.get(url);

      return { tableName, key: key || tableName, data: response.data.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error");
    }
  }
);

export const fetchRenewalCounts = createAsyncThunk(
  "dashboard/fetchRenewalCounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/get-renewals-count`);
      const { data } = response.data
      // return data?.length > 0 ? data[0]?.renewal_count : 0;
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error");
    }
  }
);

export const fetchBinderCounts = createAsyncThunk(
  "dashboard/fetchBinderCounts",
  async ({ producerCode, branchCode }: { producerCode?: string | null; branchCode?: string | null } = {}, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-binders-count`;
      const params: string[] = [];
      if (producerCode) {
        params.push(`producer_code=${producerCode}`);
      }
      if (branchCode) {
        params.push(`branch_code=${branchCode}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await api.get(url);
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error");
    }
  }
);
export const fetchTaskModuleWiseCounts = createAsyncThunk(
  "dashboard/fetchTaskModuleWiseCounts",
  async ({ producerCode, branchCode }: { producerCode?: string | null; branchCode?: string | null } = {}, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-task-module-wise-count`;
      const params: string[] = [];
      if (producerCode) {
        params.push(`producer_code=${producerCode}`);
      }
      if (branchCode) {
        params.push(`branch_code=${branchCode}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await api.get(url);
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error");
    }
  }
);

export const fetchNoticeAndEdocCounts = createAsyncThunk(
  "dashboard/fetchNoticeAndEdocCounts",
  async ({ producerCode, branchCode }: { producerCode?: string | null; branchCode?: string | null } = {}, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-notice-edocs-count`;
      const params: string[] = [];
      if (producerCode) {
        params.push(`producer_code=${producerCode}`);
      }
      if (branchCode) {
        params.push(`branch_code=${branchCode}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const response = await api.get(url);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error");
    }
  }
);

export const fetchBlogPosts = createAsyncThunk(
  "dashboard/fetchBlogPosts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        "https://www.aaxel.ca/wp-json/wp/v2/posts?per_page=10&_fields=id,date,title,link,yoast_head_json"
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching blogs");
    }
  }
);

export const fetchOutlookEvents = createAsyncThunk(
  "dashboard/fetchOutlookEvents",
  async (
    params: { startDateTime?: string; endDateTime?: string } | undefined,
    { rejectWithValue }
  ) => {
    try {
      let url = "/outlook/calendar/events";
      const queryParams = [];
      if (params?.startDateTime) queryParams.push(`startDateTime=${params.startDateTime}`);
      if (params?.endDateTime) queryParams.push(`endDateTime=${params.endDateTime}`);
      if (queryParams.length > 0) url += `?${queryParams.join("&")}`;

      const response = await api.get(url);

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching outlook events");
    }
  }
);

export const fetchOperationalUpdates = createAsyncThunk(
  "dashboard/fetchOperationalUpdates",
  async (
    { page = 1, limit = 10 }: { page?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        `/general/get-opeartional-updates?page=${page}&limit=${limit}&status=ACTIVE`
      );
      return {
        data: response.data.data,
        currentPage: page,
        hasMore: response.data.data.length === limit,
        limit,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Error fetching operational updates"
      );
    }
  }
);

// ── Customer Acquisition ──────────────────────────────────────────────────

export const fetchCustomerAcquisitionData = createAsyncThunk(
  "dashboard/fetchCustomerAcquisitionData",
  async ({ year, branchCode, producerCode }: { year: number, branchCode: string | null, producerCode: string | null }, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-customer-acquisition?year=${year}`
      if (producerCode) {
        url += `&producer_code=${producerCode}`
      }
      if (branchCode) {
        url += `&branch_code=${branchCode}`
      }
      const response = await api.get(url);
      return { data: response.data.data, year };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching customer acquisition data");
    }
  }
);


// ── Customer Acquisition ──────────────────────────────────────────────────

export const fetchGrossWrittenPremiumData = createAsyncThunk(
  "dashboard/fetchGrossWrittenPremiumData",
  async ({ year, branchCode, producerCode, policyType, insurerCode }: {
    year: number,
    branchCode: string | null, producerCode: string | null
    policyType: string | null
    insurerCode: string | null
  }, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-invoice-transactions?year=${year}`
      if (producerCode) {
        url += `&producer_one_code=${producerCode}`
      }
      if (branchCode) {
        url += `&branch_code=${branchCode}`
      }
      if (policyType) {
        url += `&policy_type=${policyType}`
      }
      if (insurerCode) {
        url += `&insurer_code=${insurerCode}`
      }
      const response = await api.get(url);
      return { data: response.data.data, year };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching customer acquisition data");
    }
  }
);
// ── Policy Retention Rate ─────────────────────────────────────────────────

export const fetchRetentionData = createAsyncThunk(
  "dashboard/fetchRetentionData",
  async (year: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/get-policy-retention?year=${year}`);

      // Robustly extract the data array, handling multiple layers of nesting
      let extractedData: any[] = [];
      const respData = response.data;

      if (Array.isArray(respData)) {
        extractedData = respData;
      } else if (respData && Array.isArray(respData.data)) {
        extractedData = respData.data;
      } else if (respData?.data && Array.isArray(respData.data.data)) {
        extractedData = respData.data.data;
      } else if (respData?.data && Array.isArray(respData.data.records)) {
        extractedData = respData.data.records;
      } else if (respData && typeof respData === 'object') {
        const potentialArray = Object.values(respData).find(Array.isArray);
        if (potentialArray) {
          extractedData = potentialArray;
        }
      }

      return { data: extractedData, year };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching retention data");
    }
  }
);


export const fetchRetentionDataV2 = createAsyncThunk(
  "dashboard/fetchRetentionDataV2",
  async ({ year, branchCode, producerCode }: { year: number, branchCode: string | null, producerCode: string | null }, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-policy-retention?year=${year}`;
      if (producerCode) {
        url += `&producer_one_code=${producerCode}`;
      }
      if (branchCode) {
        url += `&branch_code=${branchCode}`;
      }
      const response = await api.get(url);
      return { data: response.data.data, year };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching AR trend data");
    }
  }
);

// ── AR Trend ──────────────────────────────────────────────────────────────────

export const fetchARTrendData = createAsyncThunk(
  "dashboard/fetchARTrendData",
  async ({ year, branchCode, producerCode }: { year: number, branchCode: string | null, producerCode: string | null }, { rejectWithValue }) => {
    try {
      let url = `/analytics/get-ar-trend?year=${year}`;
      if (producerCode) {
        url += `&producer_code=${producerCode}`;
      }
      if (branchCode) {
        url += `&branch_code=${branchCode}`;
      }
      const response = await api.get(url);
      return { data: response.data.data, year };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Error fetching AR trend data");
    }
  }
);

// ── Customer Segmentation ─────────────────────────────────────────────────────

export const fetchCustomerSegmentation = createAsyncThunk(
  "dashboard/fetchCustomerSegmentation",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/analytics/get-customer-segmentation");
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Error fetching customer segmentation data"
      );
    }
  }
);

// ── Total Customer Count ──────────────────────────────────────────────────────

export const fetchTotalCustomerCount = createAsyncThunk(
  "dashboard/fetchTotalCustomerCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/analytics/get-customer-count");
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Error fetching total customer count"
      );
    }
  }
);
